import React from 'react';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import { hasHtmlContent, sanitize } from 'helpers/utils/htmlSanitize';
import Image from 'frontastic/lib/image';

import { GenericSectionContent, MediaCard } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import styles from './index.module.css';

/**
 * Draco — an optional section title/description above a three-up grid of beige article cards, each
 * a 4:3 image over a heading, a short excerpt and a hover-animated arrow, linking to that card's
 * first CTA. Maps the legacy `commercetools/innovation` tastic that renders the `/renovation-guide`
 * (and `/press-releases`) landing grid.
 *
 * Three legacy behaviours are deliberately NOT carried over:
 *  - the tastic derived its cards from a `contentstack/content-list` of `blog_landing_page` entries
 *    and filtered them by URL path. Draco's cards are authored, so articles already migrated to V2
 *    no longer silently drop off the landing page;
 *  - it truncated each excerpt to 150 characters at render time with `DOMParser`, which exists only
 *    in the browser. Excerpts are authored at their final length instead;
 *  - it picked its column count with `lg:grid-cols-${n}` for fewer than five cards. Tailwind cannot
 *    generate a class from an interpolated value, so that branch emitted no column class at all.
 *    The grid is fixed at 1 / 2 / 3 — what every live instance actually renders.
 */
const DracoCard: React.FC<{ card: MediaCard }> = ({ card }) => {
  const image = card?.desktop_image;
  // Only the first CTA is used: the card itself is the link, so a second one would have nowhere to go.
  const cta = card?.ctas?.[0];

  const tile = (
    // The 2px/5px/10px gutters below are the legacy geometry, not an oversight: caroma's Tailwind
    // config maps the numeric spacing scale to pixels, so the original `p-2`/`p-5`/`gap-10` are
    // 2px/5px/10px. Reproduced as-is so the grid is pixel-identical to the page it replaces.
    <div className="h-full bg-[#F3EDE9] transition-shadow hover:shadow-lg">
      {image?.url && (
        <div className="relative aspect-[4/3] w-full">
          <Image
            loading="lazy"
            src={image.url}
            alt={image?.description ?? ''}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            style={{ objectFit: 'cover' }}
            className="rounded-md"
          />
        </div>
      )}
      {card?.title && (
        <Typography as="h3" className="mt-2 p-2 text-lg font-semibold text-gray-800">
          {card.title}
        </Typography>
      )}
      {hasHtmlContent(card?.description) && (
        // `mt-2` only when there is no title above, matching the legacy spacing rule.
        <div className={`p-2 ${card?.title ? '' : 'mt-2'}`}>
          <div
            className="p-5 text-sm text-gray-600"
            dangerouslySetInnerHTML={{ __html: sanitize(card.description) }}
          />
        </div>
      )}
      <div className="mt-2 transform p-2 text-right transition-transform duration-300 group-hover:translate-x-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
          className="h-32 w-32 text-gray-600"
        >
          <path
            fillRule="evenodd"
            d="M13.293 4.293a1 1 0 011.414 0l6 6a1 1 0 01.083 1.32l-.083.094-6 6a1 1 0 01-1.497-1.32l.083-.094L17.585 12H4a1 1 0 01-.117-1.993L4 10h13.585l-4.293-4.293a1 1 0 01-.083-1.32z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    </div>
  );

  // A card without a CTA is still a valid tile — wrapping it in a link would only produce an anchor
  // pointing at "#". The arrow animates off `group`, so the group must sit on whichever is outermost.
  return cta?.url ? (
    <Link link={cta.url} target={cta?.target ?? '_self'} className="group block">
      {tile}
    </Link>
  ) : (
    <div className="group">{tile}</div>
  );
};

export default function DracoRendering({ content }: { content: GenericSectionContent }) {
  const { cards, title, description, content_alignment, additional_css, additional_css_class_names } = content;

  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'DracoRendering');

  const textAlignmentClass =
    content_alignment === 'Center' ? 'text-center' :
    content_alignment === 'Right' ? 'text-right' :
    'text-left';

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div
        className={`pt-24 pb-24 px-24 md:px-28 lg:pt-40 lg:pb-40 lg:px-48 xl:px-60 2xl:pt-60 2xl:pb-60 2xl:px-80 ${[
          additional_css_class_names,
          uniqueClass,
        ]
          .filter(Boolean)
          .join(' ')}`}
      >
        {title && (
          <Typography
            as="h2"
            className={`mb-24 text-32 font-medium leading-10 text-[#090909] xl:text-36 2xl:text-48 lg:leading-[3.25rem] ${textAlignmentClass}`}
          >
            {title}
          </Typography>
        )}
        {hasHtmlContent(description) && (
          <div
            className={`mb-24 text-16 font-normal leading-6 text-[#333] md:text-18 lg:leading-8 2xl:text-20 ${textAlignmentClass}`}
            dangerouslySetInnerHTML={{ __html: sanitize(description) }}
          />
        )}
        {cards && cards.length > 0 && (
          // CSS-responsive grid rather than a measured one, so the cards render once, server-side.
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map((card: MediaCard, index: number) => (
              <DracoCard key={index} card={card} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
